<!-- 
Dialog for displaying validation errors.

Dialog can handle:
- client-side errors, via the "form" prop
- server-side errors, via the "state-error" prop
- specific custom errors, via the "specified-error" prop

The normal validation usage pattern is:
- user fills in the form (usually on a dialog)
- simple client-side errors will be picked up as they go and displayed under the inputs (this isn't reliant on the validation dialog)
- user clicks the save button
- validate() method is called on the form to check for client-side errors; if there are any then the validation dialog's show prop is set and errors are displayed
- the appropriate vuex action is dispatched
- if the vuex dispatch returns false then the validation dialog's show prop is set and the server-side errors are displayed

See FeesDialog.vue for an example of this pattern.
-->
<template>
  <v-dialog :value="showDialog" persistent content-class="dialog sm">
    <v-card>
      <v-toolbar color="red darken-4" flat dark>
        <v-toolbar-title>
          {{ title }}
        </v-toolbar-title>
      </v-toolbar>
      <v-card-text>
        <!-- eslint-disable-next-line vue/no-v-text-v-html-on-component vue/no-v-html -->
        <div v-if="showingSecurityError" v-html="securityMessage"/>
        <spreadsheet-upload-error-table v-else-if="isSpreadsheetError" :error="stateError"/>
        <template v-else-if="isFixable">
          <p>
            Please address the following issues before continuing:
          </p>
          <ul class="error-list">
            <template v-if="displayClientValidationErrors">
              <template v-for="(error, errorIndex) in clientSideValidationErrors">
                <li v-for="(item, itemIndex) in error.errors" :key="`${errorIndex}-${itemIndex}-li`">
                  <a v-if="error.label" href="#" @click.stop.prevent="jumpTo(error.el)">
                    <span :key="`${errorIndex}-${itemIndex}-lbl`" class="font-weight-medium">{{ error.label | stripRequiredIndicator }}</span>
                  </a>
                  <span :key="`${errorIndex}-${itemIndex}-err`"> - {{ item.toLowerCase() }}.</span>
                </li>
              </template>
            </template>
            <template v-if="displaySpecifiedError">
              <li>
                {{ specifiedError }}
              </li>
            </template>
            <template v-else-if="displayServerValidationErrors">
              <li v-for="validationMessage in validationMessages" :key="validationMessage">
                {{ validationMessage }}
              </li>
            </template>
          </ul>        
        </template>
        <template v-else>
          <p>{{  specifiedError }}</p>
        </template>
        <slot/>
      </v-card-text>
      <v-card-actions>
        <v-spacer/>
        <v-btn flat class="validation-ok" @click="hide">Ok</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import _ from "underscore";
import SpreadsheetUploadErrorTable from "@/components/levy-collection-providers/SpreadsheetUploadErrorTable.vue";
import { GLOBAL_ERROR } from "@/store/system/mutation-types";
import filterMixins from "@/mixins/filter.mixins";

export default {
  components: { SpreadsheetUploadErrorTable },
  mixins: [filterMixins],
  model: {
    prop: "show",
    event: "update:show"
  },
  props: {
    show: {
      type: Boolean,
      required: true
    },
    form: {
      type: Object,
      default: null
    },
    stateError: {
      type: Object,
      default: null
    },
    specifiedError: {
      type: String,
      default: null
    },
    showSecurityErrors: {
      default: false,
      type: Boolean
    },
    fixable: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      clientSideValidationErrors: []
    };
  },
  computed: {
    showingSecurityError() {
      return this.showSecurityErrors && this.isSecurityError;
    },
    title() {
      return this.showingSecurityError ? "Security issue" : "Validation issues";
    },
    status() {
      if (this.stateError && this.stateError.details) {
        if (this.stateError.details.response) {
          return this.stateError.details.response.status;
        }
      }
      return null;
    },
    isValidationError() {
      return this.status == 400 && !this.isSpreadsheetError;
    },
    isSpreadsheetError() {
      if (this.stateError && this.stateError.details && this.stateError.details.response) {
        const d = this.stateError.details.response.data;
        const hasTitle = d && d.title ? true : false;
        return hasTitle && d.title.startsWith("Spreadsheet");
      }
      return false;
    },
    isFixable() {
      return this.fixable;
    },
    displaySpreadsheetErrors() {
      return this.stateError && this.stateError.show && this.isSpreadsheetError;
    },
    displayServerValidationErrors() {
      return this.stateError && this.stateError.show && this.isValidationError;
    },
    displaySecurityErrors() {
      return this.stateError && this.stateError.show && this.isSecurityError && this.showSecurityErrors;
    },
    displayClientValidationErrors() {
      return this.clientSideValidationErrors && this.clientSideValidationErrors.length > 0 ? true : false;
    },
    displaySpecifiedError() {
      return this.specifiedError ? true : false;
    },
    isSecurityError() {
      return this.status == 401 || this.status == 403;
    },
    validationMessages() {
      if (this.isValidationError) {
        let messages =
          this.stateError && this.stateError.details.response.data.errors
            ? this.stateError.details.response.data.errors
            : [];
        if (messages.length) {
          messages = _(messages).pluck("messages");
          messages = _(messages).flatten();
        }
        if (messages.length == 0 && this.stateError && this.stateError.details.response.data.title) {
          messages = [this.stateError.details.response.data.title];
        }
        return messages;
      } else {
        return [];
      }
    },
    securityMessage() {
      if (this.stateError && this.stateError.details) {
        const data = this.stateError.details.response ? this.stateError.details.response.data : null;
        if (data) {
          return data.title ? data.title : data;
        } else {
          if (this.status == 403) {
            return "You have insufficient permissions to perform this action";
          }
        }
      }
      return null;
    },
    showDialog() {
      return (
        this.show &&
        (this.displayServerValidationErrors ||
          this.displaySpreadsheetErrors ||
          this.displaySecurityErrors ||
          this.displayClientValidationErrors ||
          this.displaySpecifiedError)
      );
    }
  },
  watch: {
    show(nowShowing) {
      if (nowShowing) {
        if (this.form) {
          this.clientSideValidationErrors = [];
          this.form.inputs.forEach(e => {
            if (e.hasError) this.clientSideValidationErrors.push({ el: e, label: e.label, errors: e.errorBucket });
          });
        }
        if (!this.showDialog) {
          this.$store.commit(GLOBAL_ERROR, this.stateError.details);
          this.hide();
        }
      }
    }
  },
  methods: {
    hide() {
      if (this.stateError) {
        this.$store.commit(this.stateError.hideMutation, this.stateError.hideArgument);
      }
      this.$emit("update:show", false);
      this.$emit("close");
    },
    jumpTo(el) {
      this.hide();
      el.focus();
    }
  }
};
</script>
